// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ArchiveFilterControl } from '@/components/archive-filter-control';
import { HardfinTabCardWrapper } from '@/components/hardfin-tab-card-wrapper';
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import { TableControlContainer } from '@/components/table-control-container';
import { AddRounded } from '@/constants/hardfin-standard-icons';
import { SEARCH_TERM_MAXIMUM_CHARACTERS } from '@/constants/search-term';
import { Sort, invert } from '@/constants/sort';
import { ContactFilter, ContactSortProperty } from '@/contact/api/get-contacts';
import { ContactTable } from '@/contact/components/contact-table';
import { CreateContactDialog } from '@/contact/components/create-contact-dialog';
import { getCustomer } from '@/customer/api/get-customer';
import { CustomerErrorBoundary } from '@/customer/components/customer-error-boundary';
import { useDisclosure } from '@/hooks/use-disclosure';
import { Button, Stack, TextField } from '@mui/material';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { debounce } from 'lodash-es';
import { ChangeEvent, Suspense, useMemo, useState } from 'react';

const DEFAULT_SORT: Sort<ContactSortProperty> = {
  property: 'name',
  direction: 'asc',
};

const SHOW_ARCHIVED_TOOLTIP =
  'Show archived contacts for this company. Archived contacts are hidden by default.';

export const Route = createFileRoute('/_app/companies/$companyKey/contacts')({
  component: CompanyContactsRoute,
});

function CompanyContactsRoute() {
  const { companyKey } = Route.useParams();

  return (
    <CustomerErrorBoundary>
      <CompanyContactsContent companyKey={companyKey} />
    </CustomerErrorBoundary>
  );
}

function CompanyContactsContent(props: { companyKey: string }) {
  const createDialog = useDisclosure();

  const { data } = useSuspenseQuery({
    queryKey: ['customer', props.companyKey],
    queryFn: () => getCustomer(props.companyKey),
  });

  const [searchValue, setSearchValue] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [sort, setSort] = useState<Sort<ContactSortProperty>>(DEFAULT_SORT);

  const debouncedUpdate = useMemo(
    () => debounce((value: string) => setDebouncedQuery(value), 300),
    []
  );

  const filter: ContactFilter = {
    customerIds: [data.customer.id],
    search: debouncedQuery,
    isArchived: showArchived ? 'all' : undefined,
  };

  const handleSort = (property: ContactSortProperty) => {
    if (property === sort.property) {
      setSort({ property, direction: invert(sort.direction) });
      return;
    }

    setSort({ property, direction: 'asc' });
  };

  const handleChangeQuery = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
    debouncedUpdate(event.target.value);
  };

  return (
    <>
      <CreateContactDialog
        isOpen={createDialog.isOpen}
        onClose={createDialog.close}
        defaultCustomer={data.customer}
      />
      <HardfinTabCardWrapper
        title="Contacts"
        actions={
          <Button
            variant="contained"
            size="small"
            startIcon={<AddRounded />}
            onClick={createDialog.open}
          >
            Add contact
          </Button>
        }
      >
        <Stack spacing={0}>
          <TableControlContainer>
            <Stack
              direction="row"
              marginLeft="auto"
              spacing={1}
              alignItems="center"
            >
              <TextField
                size="small"
                label="Search"
                value={searchValue}
                inputProps={{ maxLength: SEARCH_TERM_MAXIMUM_CHARACTERS }}
                onChange={handleChangeQuery}
                placeholder="Search contacts"
                sx={{ width: { xs: '100%', sm: '20rem' } }}
              />
              <ArchiveFilterControl
                tooltip={SHOW_ARCHIVED_TOOLTIP}
                isChecked={showArchived}
                onChange={setShowArchived}
              />
            </Stack>
          </TableControlContainer>
          <SupportErrorBoundary message="An error occurred loading contacts">
            <Suspense>
              <ContactTable
                sort={sort}
                filter={filter}
                hideCompany
                onSort={handleSort}
              />
            </Suspense>
          </SupportErrorBoundary>
        </Stack>
      </HardfinTabCardWrapper>
    </>
  );
}
